'use client';

import { useEffect, useRef } from 'react';
import { TALK_TO_BRI_MODAL_OPEN_EVENT } from '@/components/voice/TalkToBriModalTrigger';
import {
  HERO_DEMO_SCRIPT,
  type HeroDemoLine,
} from '@/lib/marketing/messaging-bank/hero-demo-script';

/**
 * useHeroDemoAudio — the voiceover engine behind `HeroPhoneDemo`'s scripted
 * "watch Bri work" call.
 *
 * Each HERO_DEMO_SCRIPT line has a pre-generated ElevenLabs clip
 * (`line.audioSrc`). Clips are created ONLY on demo start (`beginRun`, i.e.
 * after the user's tap — the gesture that satisfies autoplay policy), so
 * nothing loads before intent and initial page weight is unchanged.
 *
 * Advancement contract (`startLineAdvance`): play line `i`'s clip and call
 * `next` when it ends (plus a short beat). EVERY failure mode — clip
 * creation, `error` event, `play()` rejection/throw, playback never
 * starting, `ended` never firing — settles exactly once onto the line's
 * pre-voiceover `talkMs` timer, so the demo runs exactly as the unvoiced
 * version and can never wedge (headless CI, strict autoplay policies).
 * After the first failure the whole run stays on timers.
 *
 * The hook also silences itself when ANY Talk-to-Bri trigger opens the live
 * voice modal (TALK_TO_BRI_MODAL_OPEN_EVENT), and pauses + releases all
 * clips on unmount.
 */

/** Beat between a clip's `ended` event and the next line starting. */
const LINE_GAP_MS = 350;
/** If a clip hasn't reached `playing` this long after `play()`, fall back. */
const AUDIO_START_TIMEOUT_MS = 4000;
/** Grace past a playing clip's known duration before the stuck-`ended` watchdog advances. */
const AUDIO_ENDED_GRACE_MS = 4000;
/** sessionStorage key — demo-audio mute persists for the session. */
const DEMO_MUTE_STORAGE_KEY = 'bizrnr-hero-demo-muted';

export function readSessionMuted(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(DEMO_MUTE_STORAGE_KEY) === '1';
  } catch {
    return false; // storage unavailable (privacy mode) — default sound on
  }
}

export function writeSessionMuted(muted: boolean) {
  try {
    window.sessionStorage.setItem(DEMO_MUTE_STORAGE_KEY, muted ? '1' : '0');
  } catch {
    // storage unavailable — mute still applies for this mount
  }
}

/** Rewind + start a clip. Module-scope (only ever called from user-gesture /
 *  timer paths, never render). May throw synchronously — callers catch. */
function restartClip(clip: HTMLAudioElement): Promise<void> | undefined {
  clip.currentTime = 0;
  return clip.play();
}

export interface HeroDemoAudio {
  /** Reset for a fresh run and lazily create the clips. Returns whether this run is voiced. */
  beginRun: (muted: boolean) => boolean;
  /** Mute/unmute every clip (mid-run safe — timing stays audio-driven while muted). */
  applyMuted: (muted: boolean) => void;
  /** Advance line `i`: audio-driven when possible, `talkMs` timer otherwise. */
  startLineAdvance: (i: number, line: HeroDemoLine, next: () => void) => void;
}

export function useHeroDemoAudio(
  scheduleTimer: (fn: () => void, ms: number) => void,
): HeroDemoAudio {
  // Voiceover clips, created lazily on demo start (never before the tap).
  const clipsRef = useRef<HTMLAudioElement[] | null>(null);
  // Once any clip fails, the rest of the run stays on talkMs timers.
  const failedRef = useRef(false);
  // Abandons the in-flight line's audio-driven advancement (modal open).
  const abortRef = useRef<(() => void) | null>(null);

  // Pause AND release (src detached) on unmount so no voiceover can outlive
  // the component.
  useEffect(() => {
    return () => {
      clipsRef.current?.forEach((clip) => {
        try {
          clip.pause();
          clip.removeAttribute('src');
          clip.load();
        } catch {
          // best-effort release
        }
      });
      clipsRef.current = null;
    };
  }, []);

  // A real voice session is about to start (any Talk-to-Bri trigger — the
  // hero FAB mid-demo, or the phone's own post-call card): silence the
  // voiceover immediately and hand the in-flight line back to its talkMs
  // fallback timer so the transcript still completes, just quietly.
  useEffect(() => {
    const silenceForModal = () => {
      failedRef.current = true;
      clipsRef.current?.forEach((clip) => {
        try {
          clip.pause();
        } catch {
          // best-effort pause
        }
      });
      abortRef.current?.();
    };
    window.addEventListener(TALK_TO_BRI_MODAL_OPEN_EVENT, silenceForModal);
    return () => window.removeEventListener(TALK_TO_BRI_MODAL_OPEN_EVENT, silenceForModal);
  }, []);

  /** Returns null when playable audio isn't available (no Audio constructor,
   *  or the runtime can't decode mp3 — e.g. jsdom), keeping the run on timers. */
  function ensureClips(): HTMLAudioElement[] | null {
    if (clipsRef.current) return clipsRef.current;
    if (typeof Audio === 'undefined') return null;
    try {
      const probe = new Audio();
      if (typeof probe.canPlayType !== 'function' || probe.canPlayType('audio/mpeg') === '') {
        return null;
      }
      clipsRef.current = HERO_DEMO_SCRIPT.map((line) => {
        const clip = new Audio(line.audioSrc);
        clip.preload = 'auto';
        return clip;
      });
      return clipsRef.current;
    } catch {
      return null;
    }
  }

  function applyMuted(muted: boolean) {
    clipsRef.current?.forEach((clip) => {
      clip.muted = muted;
    });
  }

  function beginRun(muted: boolean): boolean {
    clipsRef.current?.forEach((clip) => {
      try {
        clip.pause();
        clip.currentTime = 0;
      } catch {
        // best-effort reset (replay)
      }
    });
    failedRef.current = false;
    abortRef.current = null;
    const clips = ensureClips();
    if (clips) applyMuted(muted);
    return clips !== null;
  }

  function startLineAdvance(i: number, line: HeroDemoLine, next: () => void) {
    const clip = failedRef.current ? null : clipsRef.current?.[i];
    if (!clip) {
      scheduleTimer(next, line.talkMs);
      return;
    }

    let settled = false;
    let started = false;

    const settle = (opts: { delayMs: number; failed: boolean }) => {
      if (settled) return;
      settled = true;
      abortRef.current = null;
      clip.removeEventListener('ended', onEnded);
      clip.removeEventListener('error', onError);
      clip.removeEventListener('playing', onPlaying);
      if (opts.failed) failedRef.current = true;
      if (!clip.paused) {
        try {
          clip.pause();
        } catch {
          // best-effort pause
        }
      }
      scheduleTimer(next, opts.delayMs);
    };

    const onEnded = () => settle({ delayMs: LINE_GAP_MS, failed: false });
    const onError = () => settle({ delayMs: line.talkMs, failed: true });
    const onPlaying = () => {
      if (started) return;
      started = true;
      // Stuck-`ended` watchdog — duration is known once playback starts.
      const durationMs =
        Number.isFinite(clip.duration) && clip.duration > 0 ? clip.duration * 1000 : line.talkMs;
      scheduleTimer(
        () => settle({ delayMs: LINE_GAP_MS, failed: false }),
        durationMs + AUDIO_ENDED_GRACE_MS,
      );
    };

    clip.addEventListener('ended', onEnded);
    clip.addEventListener('error', onError);
    clip.addEventListener('playing', onPlaying);
    abortRef.current = () => settle({ delayMs: line.talkMs, failed: true });

    // Playback-never-starts watchdog (autoplay policy that neither rejects
    // nor plays, stalled network, jsdom's unimplemented play()).
    scheduleTimer(() => {
      if (!started) settle({ delayMs: line.talkMs, failed: true });
    }, AUDIO_START_TIMEOUT_MS);

    try {
      const played = restartClip(clip);
      if (played && typeof played.then === 'function') {
        played.catch(() => settle({ delayMs: line.talkMs, failed: true }));
      }
    } catch {
      settle({ delayMs: line.talkMs, failed: true });
    }
  }

  return { beginRun, applyMuted, startLineAdvance };
}
